import { initializeApp } from 'firebase/app';
import { getMessaging, getToken, onMessage } from 'firebase/messaging';
import api from './api';

const firebaseConfig = {
  apiKey: import.meta.env.VITE_FIREBASE_API_KEY || '',
  authDomain: import.meta.env.VITE_FIREBASE_AUTH_DOMAIN || '',
  projectId: import.meta.env.VITE_FIREBASE_PROJECT_ID || '',
  storageBucket: import.meta.env.VITE_FIREBASE_STORAGE_BUCKET || '',
  messagingSenderId: import.meta.env.VITE_FIREBASE_MESSAGING_SENDER_ID || '',
  appId: import.meta.env.VITE_FIREBASE_APP_ID || '',
  measurementId: import.meta.env.VITE_FIREBASE_MEASUREMENT_ID || '',
};

const isConfigValid = Object.values(firebaseConfig).every((v) => Boolean(v));
let app = null;
let messaging = null;

if (isConfigValid) {
  app = initializeApp(firebaseConfig);
  messaging = getMessaging(app);
} else if (import.meta.env.PROD) {
  // eslint-disable-next-line no-console
  console.warn(
    '[Firebase] Configuration incomplete; push notifications disabled.',
  );
}

const isSupported = () =>
  'serviceWorker' in navigator &&
  'Notification' in window &&
  'PushManager' in window;

export async function registerDeviceToken() {
  if (!messaging || !isSupported()) return null;

  const permission = await Notification.requestPermission();
  if (permission !== 'granted') {
    // eslint-disable-next-line no-console
    console.info('[Push] Notification permission not granted');
    return null;
  }

  const vapidKey = import.meta.env.VITE_FIREBASE_VAPID_KEY;
  if (!vapidKey) {
    // eslint-disable-next-line no-console
    console.warn('[Push] VAPID key missing; cannot retrieve FCM token.');
    return null;
  }

  try {
    const registration = await navigator.serviceWorker.ready;
    const currentToken = await getToken(messaging, {
      vapidKey,
      serviceWorkerRegistration: registration,
    });

    if (!currentToken) {
      // eslint-disable-next-line no-console
      console.info('[Push] No registration token available.');
      return null;
    }

    // Persist locally
    localStorage.setItem('@fcmToken', currentToken);

    // Sync with backend if user is logged in
    const token = localStorage.getItem('token');
    if (token) {
      // Temp endpoint for hardcoded admin login. Switch to PUT /admin-auth/fcm-token
      // once the admin login uses the real JWT flow.
      await api.post('/admin-auth/assign-fcm-token-temp', {
        fcmToken: currentToken,
        platform: 'web-admin',
      });
    }

    return currentToken;
  } catch (err) {
    // eslint-disable-next-line no-console
    console.error('[Push] Failed to retrieve FCM token', err);
    return null;
  }
}

export function listenForegroundMessages(handler) {
  if (!messaging) return () => {};
  return onMessage(messaging, (payload) => {
    handler?.(payload);
    if (payload?.notification?.title) {
      // Show a custom in-app toast or native notification
      const { title, body } = payload.notification;
      if (Notification.permission === 'granted') {
        new Notification(title, {
          body,
          icon: '/assets/images/logo-192.png',
          data: payload.data,
        });
      }
    }
  });
}

export { app, messaging };
